/// <amd-module name="TFS/Admin/Views/OrganizationProjects/OrganizationProjects" />
import React = require("react");
import AdminResources = require("TFS/Admin/Views/OrganizationProjects/Resources");
import * as StringUtil from "VSS/Core/Util/String";
import { Components, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { AdminTelemetry, AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { getOperationsClient } from "TFS/Admin/Common/RestClient/Operations";
import { OperationStatus } from "VSS/Platform/Contracts/Operations";
import { wait } from "VSS/Core/Util/Promise";
import { getCoreClient } from "Tfs/Platform/RestClient/Core";
import { TeamProjectReference, TeamProject } from "Tfs/Platform/Contracts/Core";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Page } from "VSSUI/Page";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { TitleSize, CustomHeader, HeaderTitleArea, HeaderTitle } from "VSSUI/Header";
import { FilterBar } from "VSSUI/FilterBar";
import { Filter } from "VSSUI/Utilities/Filter";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { ObservableValue } from "VSS/Core/Observable";
import { routeUrl } from "VSS/Platform/Location";
import { OrganizationProjectsTableComponent } from "TFS/Admin/Views/OrganizationProjects/OrganizationProjectsTable";
import {
    Operations,
    OpConstants,
    OperationPair,
    IUpdateProjectNode,
    IOrganizationProjectsData,
    OrganizationProjectReference,
    OrganizationProjectsConstants,
    IOrganizationProjectsComponentState
} from "TFS/Admin/Views/OrganizationProjects/Contracts/DataModels";
import { SearchStatus } from "TFS/Admin/Groups/Common/Constants";
import { ITableRow } from "VSSUI/Table";
import { Card } from "VSSUI/Card";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { validateProjectName } from "TFS/Admin/Common/Util/NameValidator";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import { Observer } from "VSSUI/Observer";
import { ProjectsRestoreTableComponent } from "TFS/Admin/Views/OrganizationProjects/OrganizationProjectsRestoreTable";
import { ProjectState } from "Tfs/Platform/Contracts/Core";
import { Tooltip } from "VSSUI/TooltipEx";
import { Link } from "VSSUI/Link";
import { IconSize } from "VSSUI/Icon";
import { Button } from "VSSUI/Button";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IRenameDialogProps } from "TFS/Admin/Common/Controls/RenameDialog/RenameDialog.Props";
import { IDeleteMultiDialogProps } from "TFS/Admin/Common/Controls/DeleteMultiDialog/DeleteMultiDialog.Props";
import { IDeleteProjectDialogProps } from "TFS/Admin/Common/Controls/DeleteProjectDialog/DeleteProjectDialog.Props";

export class OrganizationProjectsComponent extends VssComponent<{}, IOrganizationProjectsComponentState> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);
    private performanceRecorded = false;
    private filter: Filter;
    private _operationPair: OperationPair[];
    private _errorOperationPair: OperationPair[];
    private messagesService: IGlobalMessagesService;
    protected multiDeleteProjects: OrganizationProjectReference[];
    protected multiDeleteNoPermission: OrganizationProjectReference[];
    protected multiRestoreProjects: OrganizationProjectReference[];
    protected multiRestoreErrorProjects: OrganizationProjectReference[];
    private organizationName: string;
    private clearSelection: ObservableValue<boolean>;
    protected updateNode: IUpdateProjectNode = {};
    protected targetRenameProject: OrganizationProjectReference | null;
    protected selectedRestore: ObservableValue<boolean>;

    private static readonly OrganizationProjectsRestoreTabFeatureFlag = "VisualStudio.Services.AdminEngagement.Projects.EnableProjectRestoreTab";
    protected isRestoreTableEnabled: boolean;

    public constructor(props: {}, context?: any) {
        super(props, context);

        this.state = {
            projects: [],
            softDeletedProjects: [],
            currentOperation: OpConstants.IDLE,
            isInProgress: false,
            error: "",
            showErrorMessage: false,
            showRestoreDialog: false,
            searchResult: {
                keyword: "",
                resultItems: [],
                status: SearchStatus.None,
                hasMore: false
            }
        };

        this.clearSelection = new ObservableValue<boolean>(false);
        this.targetRenameProject = null;
        this.multiDeleteProjects = [];
        this.multiDeleteNoPermission = [];
        this.multiRestoreProjects = [];
        this.multiRestoreErrorProjects = [];
        this._operationPair = [];
        this._errorOperationPair = [];
        this.filter = new Filter();
        this.messagesService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        this.organizationName = vssPageData != undefined ? vssPageData.hostName : AdminResources.Confirm;
        this.selectedRestore = new ObservableValue(false);
        this.isRestoreTableEnabled = isFeatureFlagEnabled(
            this.context.pageContext,
            OrganizationProjectsComponent.OrganizationProjectsRestoreTabFeatureFlag,
            false
        );
    }

    public componentDidMount() {
        this._getProjects();
    }

    public render(): JSX.Element {
        if (this.state.projects.length > 0 && !this.performanceRecorded) {
            const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.markTimeToInteractive(Area.Admin, Feature.OrganizationProjects);
            this.performanceRecorded = true;
        }

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow flex-column projects-page">
                    <CustomHeader className="bolt-header-with-commandbar">
                        <HeaderTitleArea className="flex-column">
                            <HeaderTitle className="text-ellipsis" titleSize={TitleSize.Large}>
                                <Observer selectedRestore={this.selectedRestore}>
                                    {(props: { selectedRestore: boolean }) => {
                                        return props.selectedRestore ? (
                                            <div className="restore-title-div">
                                                <Button
                                                    subtle={true}
                                                    tooltipProps={{ text: AdminResources.ProjectTitle }}
                                                    className="bolt-header-command-item-button back-icon"
                                                    iconProps={{ iconName: "Back", size: IconSize.medium }}
                                                    onClick={this.onSelectedTableChanged}
                                                />
                                                <span>{AdminResources.RecentlyDeleteProjectsTitle}</span>
                                            </div>
                                        ) : (
                                            AdminResources.ProjectTitle
                                        );
                                    }}
                                </Observer>
                            </HeaderTitle>
                        </HeaderTitleArea>
                        <div className="flex-row projects-filter-container justify-end">
                            <FilterBar filter={this.filter} className="projects-filter">
                                <KeywordFilterBarItem placeholder={AdminResources.FilterProjects} filterItemKey="name" />
                            </FilterBar>
                        </div>
                    </CustomHeader>
                    <div className="flex-column">
                        {this.state.showErrorMessage && this._showActionErrorMessage()}

                        <Observer selectedRestore={this.selectedRestore}>
                            {(props: { selectedRestore: boolean }) => {
                                switch (props.selectedRestore) {
                                    case false:
                                        return (
                                            <>
                                                <Card className="projects-card justify-center">
                                                    <OrganizationProjectsTableComponent
                                                        OnDeleteProject={this._onDeleteProjectClick}
                                                        OnRenameProject={this._onEditProjectClick}
                                                        OnCreateProject={this._onAddProjectClick}
                                                        onMultiProjectDelete={this._onMultiProjectDelete}
                                                        OnRowClicked={this._onRowClicked}
                                                        OnSearchProject={this._OnSearchProject}
                                                        ClearSelection={this.clearSelection}
                                                        Projects={this.state.projects}
                                                        IsInProgress={this.state.isInProgress}
                                                        filter={this.filter}
                                                        pageContext={this.context.pageContext}
                                                        searchResult={this.state.searchResult}
                                                    />
                                                </Card>

                                                {this.isRestoreTableEnabled && this.state.softDeletedProjects.length > 0 && (
                                                    <Tooltip overflowOnly={true}>
                                                        <span className="restore-projects-span text-ellipsis">
                                                            <Link
                                                                aria-label={AdminResources.ViewRecentlyDeleteProjects}
                                                                onClick={this.onSelectedTableChanged}
                                                            >
                                                                {AdminResources.ViewRecentlyDeleteProjects}
                                                            </Link>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </>
                                        );
                                    case true:
                                        return (
                                            <Card className="projects-card justify-center">
                                                <ProjectsRestoreTableComponent
                                                    onMultiProjectRestore={this._onMultiProjectRestore}
                                                    OnRestoreProject={this._onRestoreProjectClick}
                                                    OnSearchProject={this._OnSearchProject}
                                                    filter={this.filter}
                                                    searchResult={this.state.searchResult}
                                                    ClearSelection={this.clearSelection}
                                                    Projects={this.state.softDeletedProjects}
                                                    IsInProgress={this.state.isInProgress}
                                                    pageContext={this.context.pageContext}
                                                />
                                            </Card>
                                        );
                                }
                            }}
                        </Observer>

                        {this.state.currentOperation == OpConstants.RENAME && !this.state.isInProgress && (
                            <WrappedComponent<IRenameDialogProps>
                                dependencies={["ms.vss-admin-web.admin-common"]}
                                wrappedType={"ms.vss-admin-web.rename-dialog"}
                                wrappedProps={{
                                    name: this.targetRenameProject ? this.targetRenameProject.name : "",
                                    onCancel: () => this.setState({ currentOperation: OpConstants.IDLE }),
                                    onRename: this._onRenameProjectConfirmed,
                                    renameDialogTitle: AdminResources.RenameProjectDialogTitle,
                                    renameDialog1: AdminResources.RenameProjectDialog1,
                                    renameDialog2: AdminResources.RenameProjectDialog2,
                                    renameDialog2Link: AdminResources.RenameProjectDialog2Link,
                                    renameConfirmationText: AdminResources.RenameProjectConfirmationText,
                                    renameNewName: AdminResources.RenameProjectNewName,
                                    invalidNameMessage: AdminResources.InvalidProjectNameMessage,
                                    validateName: validateProjectName
                                }}
                            />
                        )}
                        {this.state.currentOperation == OpConstants.DELETE && !this.state.isInProgress && (
                            <WrappedComponent<IDeleteProjectDialogProps>
                                dependencies={["ms.vss-admin-web.admin-common"]}
                                wrappedType={"ms.vss-admin-web.delete-project-dialog"}
                                wrappedProps={{
                                    onCancel: () => this.setState({ currentOperation: OpConstants.IDLE }),
                                    onDeleteProject: this._onDeleteProjectConfirmed,
                                    pageContext: this.context.pageContext,
                                    projectName:
                                        this.multiDeleteProjects.length > 0 ? this.multiDeleteProjects[this.multiDeleteProjects.length - 1].name : ""
                                }}
                            />
                        )}
                        {this.state.currentOperation == OpConstants.ADD && !this.state.isInProgress && (
                            <WrappedComponent
                                dependencies={["ms.vss-tfs-web.create-project-panel"]}
                                wrappedType="createProjectPanel"
                                showPanel={true}
                                invokingSource={"OrganizationSettingsProjects"}
                                onPanelDismiss={this._onCancelCreateProject}
                                onCreateProjectComplete={this._onCreateProjectComplete}
                                skipRedirectToDetails={true}
                            />
                        )}
                        {this.state.currentOperation == OpConstants.MULTIDELETE && !this.state.isInProgress && (
                            <WrappedComponent<IDeleteMultiDialogProps>
                                dependencies={["ms.vss-admin-web.admin-common"]}
                                wrappedType={"ms.vss-admin-web.delete-multi-dialog"}
                                wrappedProps={{
                                    onCancel: this._onCancelMultiDelete,
                                    onDeleteProject: this._sendMultiDeleteRequest,
                                    projectNames: this.multiDeleteProjects.map((item: OrganizationProjectReference) => item.name),
                                    noPermissionProjectNames: this.multiDeleteNoPermission.map((item: OrganizationProjectReference) => item.name),
                                    organizationName: this.organizationName
                                }}
                            />
                        )}
                        {this.state.showRestoreDialog && !this.state.isInProgress && (
                            <WrappedComponent
                                dependencies={["ms.vss-admin-web.admin-common"]}
                                wrappedType={"ms.vss-admin-web.restore-multi-dialog"}
                                onCancel={this._onCancelMultiRestore}
                                onRestoreProject={this._sendMultiRestoreRequest}
                                restorableProjects={this.multiRestoreProjects.map((item: OrganizationProjectReference) => item.name)}
                                unrestorableProjects={this.multiRestoreErrorProjects.map((item: OrganizationProjectReference) => item.name)}
                            />
                        )}
                    </div>
                </Page>
            </Surface>
        );
    }

    private onSelectedTableChanged = () => {
        this.clearSelection.value = true;
        this.selectedRestore.value = !this.selectedRestore.value;
        this.clearSelection.value = false;
    };

    protected _onRenameProjectConfirmed = (newName?: string): void => {
        var telemetry = this._startActionCapture("Rename Project Confirmed and Rquested", {});
        this.updateNode.name = newName;
        this._sendRequest();
    };

    protected _onDeleteProjectConfirmed = (): void => {
        var telemetry = this._startActionCapture("Delete Project Confirmed and Rquested", {});
        this._sendRequest();
    };

    private _sendRequest = (): void => {
        var telemetry = this._startActionCapture("Send Project Update Request", {});
        const restClient = getCoreClient(this.context.pageContext);
        switch (this.state.currentOperation) {
            case OpConstants.MULTIRESTORE:
                this.multiRestoreProjects.forEach(project => {
                    const partialProject: IUpdateProjectNode = { state: ProjectState.WellFormed, name: project.name };
                    const projectUpdate: Partial<TeamProject> = partialProject;
                    restClient.updateProject(projectUpdate as TeamProject, project.id).then(
                        operf => {
                            this._operationPair.push({ projectName: project.name, operationId: operf.id, projectId: project.id });
                            if (this._operationPair.length + this._errorOperationPair.length == this.multiRestoreProjects.length) {
                                this._monitorProgress(0);
                                this.setState({ isInProgress: true, showRestoreDialog: false });
                            }
                        },
                        reason => {
                            this._errorOperationPair.push({ projectName: project.name, operationId: "0", projectId: project.id });
                            telemetry.captureError("Restore Project Request Error");
                            if (this._operationPair.length + this._errorOperationPair.length == this.multiRestoreProjects.length) {
                                if (this._operationPair.length > 0) {
                                    this._monitorProgress(0);
                                    this.setState({
                                        isInProgress: true,
                                        error: reason.message || String(reason),
                                        showErrorMessage: true,
                                        showRestoreDialog: false
                                    });
                                } else {
                                    this.setState({
                                        currentOperation: OpConstants.IDLE,
                                        error: reason.message || String(reason),
                                        showErrorMessage: true,
                                        showRestoreDialog: false
                                    });
                                }
                            }
                        }
                    );
                });

                break;
            case OpConstants.MULTIDELETE:
            case OpConstants.DELETE:
                this.multiDeleteProjects.forEach(project => {
                    restClient.queueDeleteProject(project.id).then(
                        operf => {
                            this._operationPair.push({ projectName: project.name, operationId: operf.id, projectId: project.id });
                            if (this._operationPair.length + this._errorOperationPair.length == this.multiDeleteProjects.length) {
                                this._monitorProgress(0);
                                this.setState({ isInProgress: true });
                            }
                        },
                        reason => {
                            this._errorOperationPair.push({ projectName: project.name, operationId: "0", projectId: project.id });
                            telemetry.captureError("Delete Project Request Error");
                            if (this._operationPair.length + this._errorOperationPair.length == this.multiDeleteProjects.length) {
                                if (this._operationPair.length > 0) {
                                    this._monitorProgress(0);
                                    this.setState({ isInProgress: true, error: reason.message || String(reason), showErrorMessage: true });
                                } else {
                                    this.setState({
                                        currentOperation: OpConstants.IDLE,
                                        error: reason.message || String(reason),
                                        showErrorMessage: true
                                    });
                                }
                            }
                        }
                    );
                });
                break;
            case OpConstants.RENAME:
                if (this.targetRenameProject != null) {
                    const targetProjectName = this.targetRenameProject.name;
                    const targetProjectId = this.targetRenameProject.id;
                    restClient.updateProject(this.updateNode as TeamProject, this.targetRenameProject.id).then(
                        operf => {
                            this._operationPair.push({ projectName: targetProjectName, operationId: operf.id, projectId: targetProjectId });
                            this._monitorProgress(0);
                            this.setState({ isInProgress: true });
                        },
                        reason => {
                            this.setState({
                                currentOperation: OpConstants.IDLE,
                                error: reason.message || String(reason),
                                showErrorMessage: true
                            });
                            telemetry.captureError("Rename Project Request Error");
                        }
                    );
                }
                break;
        }
    };

    private _monitorProgress = async (updateCount: number): Promise<void> => {
        if (OrganizationProjectsConstants.UpdateCheckCount > updateCount) {
            const operationsClient = getOperationsClient(this.context.pageContext);
            let operationSucceeded = false;
            let operationError = false;
            const operation = this._operationPair.pop();
            if (operation == undefined) {
                this._monitorProcessError();
            } else {
                try {
                    const op = await operationsClient.getOperation(operation.operationId);
                    switch (op.status) {
                        case OperationStatus.Succeeded:
                            operationSucceeded = true;
                            break;
                        case OperationStatus.Cancelled:
                        case OperationStatus.Failed:
                            operationError = true;
                            break;
                    }
                } catch {
                    operationError = true;
                }

                if (operationError) {
                    this._errorOperationPair.push(operation);
                } else if (!operationSucceeded) {
                    this._operationPair.push(operation);
                }

                if (this._operationPair.length == 0) {
                    switch (this.state.currentOperation) {
                        case OpConstants.RENAME:
                            this._onRenameProjectComplete();
                            break;
                        case OpConstants.MULTIDELETE:
                        case OpConstants.DELETE:
                            this._onDeleteProjectComplete();
                            break;
                        case OpConstants.MULTIRESTORE:
                            this._onRestoreProjectComplete();
                            break;
                    }
                    this.setState({ currentOperation: OpConstants.IDLE, isInProgress: false });
                    this._monitorProcessCleanUp();
                } else {
                    await wait(OrganizationProjectsConstants.ProgressPollInterval);
                    await this._monitorProgress(updateCount + 1);
                }
            }
        } else {
            this._monitorProcessError();
        }
    };

    private _monitorProcessCleanUp = () => {
        this._clearOperationPair();
        this.updateNode = {};
        this.targetRenameProject = null;
        this.multiDeleteProjects = [];
        this.multiDeleteNoPermission = [];
    };

    private _monitorProcessError = () => {
        var telemetry = this._startActionCapture("Monitor Operation Error", {});
        this._monitorProcessCleanUp();
        this.setState({
            error: AdminResources.SaveError,
            isInProgress: false,
            currentOperation: OpConstants.IDLE,
            showErrorMessage: true
        });
    };

    private _clearOperationPair = () => {
        this._operationPair = [];
        this._errorOperationPair = [];
    };

    private _handleErrorProcesses = (opeation: Operations) => {
        if (this._errorOperationPair.length > 0) {
            var projectList = this._errorOperationPair.map(error => error.projectName).join(" ,");
            this.setState({
                error: StringUtil.format(AdminResources.ProcessError, projectList),
                showErrorMessage: true
            });
        }
    };

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.OrganizationProjects, Feature.OrganizationProjects, action, properties);
    };

    private _getProjects = () => {
        var telemetry = this._startActionCapture("Fetch Projects from Data Provider", {});
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        var dataProviderData = contributionService.getData<IOrganizationProjectsData>(OrganizationProjectsConstants.ContributionService)!;
        if (dataProviderData) {
            var currentProjects = this.state.projects;
            if (dataProviderData.projects) {
                dataProviderData.projects.forEach(project => {
                    var projectWithPermission = project.teamProjectReference as OrganizationProjectReference;
                    projectWithPermission.hasDeletePermission = project.hasDeletePermission;
                    projectWithPermission.hasRenamePermission = project.hasRenamePermission;
                    projectWithPermission.processTemplateName = project.processTemplateName;

                    // Adding date attribute to avoid caching issue while the image is rendering.
                    projectWithPermission.defaultTeamImageUrl = project.teamProjectReference.defaultTeamImageUrl + `&` + new Date().getTime();
                    currentProjects.push(projectWithPermission);
                });
            }

            var currentDeletedProjects = this.state.softDeletedProjects;
            if (dataProviderData.deletedProjects) {
                dataProviderData.deletedProjects.forEach(softDeletedProject => {
                    var projectWithPermission = softDeletedProject.teamProjectReference as OrganizationProjectReference;
                    projectWithPermission.hasDeletePermission = softDeletedProject.hasDeletePermission;
                    projectWithPermission.hasRenamePermission = softDeletedProject.hasRenamePermission;
                    projectWithPermission.processTemplateName = softDeletedProject.processTemplateName;

                    // Adding date attribute to avoid caching issue while the image is rendering.
                    projectWithPermission.defaultTeamImageUrl = softDeletedProject.teamProjectReference.defaultTeamImageUrl + `&` + new Date().getTime();
                    currentDeletedProjects.push(projectWithPermission);
                });
            }

            this.setState({ projects: currentProjects, softDeletedProjects: currentDeletedProjects });
        } else {
            telemetry.captureTelemetry({
                NumberOfProjects: this.state.projects.length,
                NumberofDeletedProjects: this.state.softDeletedProjects.length
            });
        }
    };

    private _onMultiProjectDelete = (projects: OrganizationProjectReference[]) => {
        var telemetry = this._startActionCapture("Projects Multi Delete Clicked", {});
        this._clearOperationPair();
        this.multiDeleteProjects = projects.filter(s => s.hasDeletePermission != undefined && s.hasDeletePermission);
        this.multiDeleteNoPermission = projects.filter(s => s.hasDeletePermission == undefined || !s.hasDeletePermission);
        this.setState({
            currentOperation: OpConstants.MULTIDELETE
        });
    };

    private _onCancelMultiDelete = (): void => {
        this.setState({ currentOperation: OpConstants.IDLE, isInProgress: false });
    };

    private _sendMultiDeleteRequest = (): void => {
        var telemetry = this._startActionCapture("Multi Projects Delete Confirmed and Rquested", {});
        this.setState({ showRestoreDialog: false });
        this.clearSelection.value = true;
        this._sendRequest();
        this.clearSelection.value = false;
    };

    private _onMultiProjectRestore = (projects: OrganizationProjectReference[]) => {
        var telemetry = this._startActionCapture("Project Multi Restore Clicked", {});
        this._clearOperationPair();
        this.multiRestoreErrorProjects = [];
        this.multiRestoreProjects = [];

        projects.forEach(project => {
            if (this.state.projects.map(p => p.name).indexOf(project.name) == -1) {
                this.multiRestoreProjects.push(project);
            } else {
                this.multiRestoreErrorProjects.push(project);
            }
        });

        if (this.multiRestoreErrorProjects.length === 0) {
            this.setState(
                {
                    currentOperation: OpConstants.MULTIRESTORE
                },
                () => this._sendMultiRestoreRequest()
            );
        } else {
            this.setState({
                showRestoreDialog: true,
                currentOperation: OpConstants.MULTIRESTORE
            });
        }
    };

    private _onCancelMultiRestore = (): void => {
        this.setState({ currentOperation: OpConstants.IDLE, isInProgress: false, showRestoreDialog: false });
    };

    private _sendMultiRestoreRequest = (): void => {
        var telemetry = this._startActionCapture("Multi Projects Restore Confirmed and Rquested", {});
        this.clearSelection.value = true;
        this._sendRequest();
        this.clearSelection.value = false;
    };

    protected _onRestoreProjectClick = (project: TeamProjectReference) => {
        var telemetry = this._startActionCapture("Restore Project Clicked", { projectId: project.id });
        this._clearOperationPair();
        this.multiRestoreErrorProjects = [];
        this.multiRestoreProjects = [];
        if (this.state.projects.map(p => p.name).indexOf(project.name) === -1) {
            this.multiRestoreProjects.push(project);
            this.setState(
                {
                    currentOperation: OpConstants.MULTIRESTORE
                },
                () => this._sendMultiRestoreRequest()
            );
        } else {
            this.multiRestoreErrorProjects.push(project);
            this.setState({
                showRestoreDialog: true,
                currentOperation: OpConstants.MULTIRESTORE
            });
        }
    };

    private _OnSearchProject = (projects: OrganizationProjectReference[], newKeyword?: string, oldKeyword?: string, scopeDescriptor?: string) => {
        if (!newKeyword || newKeyword === "") {
            this.updateSearchStatus(SearchStatus.None);
        } else if (oldKeyword !== undefined) {
            this.getSearchResult(newKeyword, projects);
        } else {
            this.updateSearchStatus(SearchStatus.Completed);
        }
    };

    private getSearchResult(keyword: string, source: OrganizationProjectReference[]) {
        const resultProjects: OrganizationProjectReference[] = [];

        for (const project of source) {
            if (StringUtil.caseInsensitiveContains(project.name, keyword)) {
                resultProjects.push(project);
            }
        }
        this.updateSearchResult(keyword, resultProjects, false);
    }

    private updateSearchResult = (keyword: string, results: any[], hasMore: boolean) => {
        if (!keyword || !this.state.searchResult) {
            return;
        }
        this.setState({
            searchResult: {
                keyword,
                resultItems: results,
                status: SearchStatus.Completed,
                hasMore
            }
        });
    };

    private updateSearchStatus = (searchStatus: SearchStatus) => {
        if (!this.state.searchResult) {
            return;
        }
        var newSearchResult = this.state.searchResult;
        newSearchResult.status = searchStatus;
        this.setState({
            searchResult: newSearchResult
        });
    };

    private _onRowClicked = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<TeamProjectReference>) => {
        var telemetry = this._startActionCapture("Navigate to Project Overview Page", { projectId: tableRow.data.id });
        if (tableRow && tableRow.data && tableRow.data.url) {
            var url = routeUrl(this.context.pageContext, "ms.vss-admin-web.project-admin-hub-route", { project: tableRow.data.name });
            FastPageSwitch(this.context.pageContext, url, true);
        } else {
            telemetry.captureError("Url unavailable for fast page switch");
        }
    };

    private _onEditProjectClick = (project: TeamProjectReference) => {
        var telemetry = this._startActionCapture("Rename Project Clicked", { projectId: project.id });
        this.targetRenameProject = project;
        this._clearOperationPair();
        if (this.state.currentOperation !== OpConstants.RENAME) {
            this.setState({
                currentOperation: OpConstants.RENAME
            });
        }
    };

    private _onDeleteProjectClick = (project: TeamProjectReference) => {
        var telemetry = this._startActionCapture("Delete Project Clicked", { projectId: project.id });
        this.multiDeleteProjects = [project];
        this._clearOperationPair();
        if (this.state.currentOperation !== OpConstants.DELETE) {
            this.setState({
                currentOperation: OpConstants.DELETE
            });
        }
    };

    private _onAddProjectClick = () => {
        var telemetry = this._startActionCapture("Create Project Clicked", {});
        if (this.state.currentOperation !== OpConstants.ADD) {
            this.setState({ currentOperation: OpConstants.ADD });
        }
    };

    private _onCancelCreateProject = () => {
        this.setState({ currentOperation: OpConstants.IDLE });
    };

    protected _onCreateProjectComplete = (newProjectName: string) => {
        const restClient = getCoreClient(this.context.pageContext);
        var actionTelemetryLogger = this._startActionCapture("Create Project Completed", {});
        restClient
            .getProject(newProjectName, true)
            .then(
                newProject => {
                    var extendNewProject = newProject as OrganizationProjectReference;
                    extendNewProject.hasDeletePermission = true;
                    extendNewProject.hasRenamePermission = true;
                    extendNewProject.processTemplateName = newProject.capabilities["processTemplate"]["templateName"];
                    var currentProjects = this.state.projects;
                    var currentSearchResult = this.state.searchResult;
                    currentProjects.unshift(extendNewProject);
                    if (
                        currentSearchResult.status == SearchStatus.Completed &&
                        StringUtil.caseInsensitiveContains(newProjectName, currentSearchResult.keyword)
                    ) {
                        currentSearchResult.resultItems.push(extendNewProject);
                        this.setState({ searchResult: currentSearchResult });
                    }
                    this.setState({ currentOperation: OpConstants.IDLE });
                    this._onQueueToast(StringUtil.format(AdminResources.CreateSucceededMessage, newProjectName));
                },
                reason => {
                    actionTelemetryLogger.captureError("Get Project Request Error");
                    this.setState({ currentOperation: OpConstants.IDLE, error: reason.message || String(reason), showErrorMessage: true });
                }
            )
            .catch(reason => {
                actionTelemetryLogger.captureError("Get Project Request Error");
                this.setState({ currentOperation: OpConstants.IDLE, error: reason.message || String(reason), showErrorMessage: true });
            });
    };

    protected _onRenameProjectComplete = () => {
        var actionTelemetryLogger = this._startActionCapture("Rename Project Completed", {});
        const errorProjects = this._errorOperationPair.map(error => error.projectName);
        if (this.targetRenameProject && this.updateNode && this.updateNode.name && errorProjects.indexOf(this.targetRenameProject.name) == -1) {
            var currName = this.targetRenameProject.name;
            var currentSearchResult = this.state.searchResult;
            if (currentSearchResult.status == SearchStatus.Completed && StringUtil.caseInsensitiveContains(currName, currentSearchResult.keyword)) {
                var filterResult = currentSearchResult.resultItems as OrganizationProjectReference[];
                var index = filterResult.map((item: OrganizationProjectReference) => item.name).indexOf(currName);
                if (!StringUtil.caseInsensitiveContains(this.updateNode.name, currentSearchResult.keyword)) {
                    index != -1 && currentSearchResult.resultItems.splice(index, 1);
                } else {
                    index != -1 && (currentSearchResult.resultItems[index] = this.targetRenameProject);
                }
                this.setState({ searchResult: currentSearchResult });
            }

            this.targetRenameProject.name = this.updateNode.name;
            this._onQueueToast(StringUtil.format(AdminResources.RenameSucceededMessage, currName, this.updateNode.name));
        }
        this._handleErrorProcesses(OpConstants.RENAME);
        this._clearOperationPair();
    };

    private _onDeleteProjectComplete = () => {
        var actionTelemetryLogger = this._startActionCapture("Delete Project Completed", {});
        const errorProjects = this._errorOperationPair.map(error => error.projectId);
        this.multiDeleteProjects.forEach(project => {
            if (errorProjects.indexOf(project.id) == -1) {
                // Delete project from valid list
                var targetProjectId = project.id;
                var targetProjectName = project.name;
                var index = this.state.projects.map((project: OrganizationProjectReference) => project.id).indexOf(targetProjectId);
                var currentProjects = this.state.projects;
                index != -1 && currentProjects.splice(index, 1);

                // Delete project from current filter
                var currentSearchResult = this.state.searchResult;
                if (
                    currentSearchResult.status == SearchStatus.Completed &&
                    StringUtil.caseInsensitiveContains(targetProjectName, currentSearchResult.keyword)
                ) {
                    var filterResult = currentSearchResult.resultItems as OrganizationProjectReference[];
                    var index = filterResult.map((item: OrganizationProjectReference) => item.name).indexOf(targetProjectName);
                    index != -1 && currentSearchResult.resultItems.splice(index, 1);
                    this.setState({ searchResult: currentSearchResult });
                }

                // Add project to softDeletedList
                var currentSoftDeletedProjects = this.state.softDeletedProjects;
                currentSoftDeletedProjects.unshift(project);
                this.setState({ projects: currentProjects, softDeletedProjects: currentSoftDeletedProjects });
                this._onQueueToast(StringUtil.format(AdminResources.DeleteSucceededMessage, targetProjectName));
            }
        });
        this._handleErrorProcesses(OpConstants.DELETE);
        this._clearOperationPair();
    };

    private _onRestoreProjectComplete = () => {
        var actionTelemetryLogger = this._startActionCapture("Restore Project Completed", {});
        const errorProjects = this._errorOperationPair.map(error => error.projectId);
        this.multiRestoreProjects.forEach(project => {
            if (errorProjects.indexOf(project.id) == -1) {
                // Remove project from restore list
                var targetProjectId = project.id;
                var targetProjectName = project.name;
                var index = this.state.softDeletedProjects.map((project: OrganizationProjectReference) => project.id).indexOf(targetProjectId);
                var currentSoftDeletedProjects = this.state.softDeletedProjects;
                index != -1 && currentSoftDeletedProjects.splice(index, 1);

                // Remove project from current filter list
                var currentSearchResult = this.state.searchResult;
                if (
                    currentSearchResult.status == SearchStatus.Completed &&
                    StringUtil.caseInsensitiveContains(targetProjectName, currentSearchResult.keyword)
                ) {
                    var filterResult = currentSearchResult.resultItems as OrganizationProjectReference[];
                    var index = filterResult.map((item: OrganizationProjectReference) => item.name).indexOf(targetProjectName);
                    index != -1 && currentSearchResult.resultItems.splice(index, 1);
                    this.setState({ searchResult: currentSearchResult });
                }

                // Add project to valid project list
                var currentProjects = this.state.projects;
                currentProjects.unshift(project);
                this.setState({ softDeletedProjects: currentSoftDeletedProjects, projects: currentProjects });
                this._onQueueToast(StringUtil.format(AdminResources.RestoreSucceededMessage, targetProjectName));
            }
        });
        this._handleErrorProcesses(OpConstants.MULTIRESTORE);
        this._clearOperationPair();
    };

    protected _onQueueToast = (toastMessage: string) => {
        this.messagesService.addToast({
            message: toastMessage,
            duration: 3500
        });
    };

    private _showActionErrorMessage() {
        return (
            <MessageCard
                className="message-warning-card"
                severity={MessageCardSeverity.Error}
                onDismiss={() => this.setState({ showErrorMessage: false })}
            >
                <FormatComponent format={this.state.error} />
            </MessageCard>
        );
    }

    public componentDidUpdate(): void {}
}

Components.add("organizationProjects", OrganizationProjectsComponent);
